import { useCallback, useEffect, useRef, useState } from 'react'
import { ProfileColumnDef } from './types'

interface UseProfileTableResizeOptions<TMiddleKey extends string> {
    getColumnWidth: (column: TMiddleKey) => number
    columnDefinitionMap: Record<string, ProfileColumnDef<string>>
    onResize: (column: TMiddleKey, width: number) => void
}

export const useProfileTableResize = <TMiddleKey extends string>({
    getColumnWidth,
    columnDefinitionMap,
    onResize,
}: UseProfileTableResizeOptions<TMiddleKey>) => {
    const [resizingColumn, setResizingColumn] = useState<TMiddleKey | null>(null)
    const resizingRef = useRef<{ column: TMiddleKey; startX: number; startWidth: number } | null>(null)
    const resizeListenersRef = useRef<{ move: (event: MouseEvent) => void; up: () => void } | null>(null)

    const tearDownResizeListeners = useCallback(() => {
        if (resizeListenersRef.current) {
            document.removeEventListener('mousemove', resizeListenersRef.current.move)
            document.removeEventListener('mouseup', resizeListenersRef.current.up)
        }
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        resizeListenersRef.current = null
        resizingRef.current = null
        setResizingColumn(null)
    }, [])

    useEffect(() => {
        return () => {
            tearDownResizeListeners()
        }
    }, [tearDownResizeListeners])

    const handleResizeStart = useCallback(
        (event: React.MouseEvent, column: TMiddleKey) => {
            event.preventDefault()
            event.stopPropagation()
            tearDownResizeListeners()

            const definition = columnDefinitionMap[column]
            const startWidth = getColumnWidth(column)
            resizingRef.current = { column, startX: event.clientX, startWidth }
            setResizingColumn(column)

            const handleMouseMove = (mouseEvent: MouseEvent) => {
                const activeResize = resizingRef.current
                if (!activeResize) return

                const diff = mouseEvent.clientX - activeResize.startX
                const nextWidth = Math.max(definition.minWidth, Math.min(definition.maxWidth ?? Number.POSITIVE_INFINITY, activeResize.startWidth + diff))
                onResize(activeResize.column, nextWidth)
            }

            const handleMouseUp = () => {
                tearDownResizeListeners()
            }

            document.body.style.cursor = 'col-resize'
            document.body.style.userSelect = 'none'
            resizeListenersRef.current = { move: handleMouseMove, up: handleMouseUp }
            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
        },
        [columnDefinitionMap, getColumnWidth, onResize, tearDownResizeListeners]
    )

    return { resizingColumn, handleResizeStart }
}
